// ═══════════════════════════════════════════════════════════════════════════
// DUAL-RUN — Fase 3B, paso 2 del cutover. Lee crm_actividad_resumen_grupos/
// ..._contactos EN PARALELO al camino viejo (getCrmActividades.js), compara
// por cliente y loguea con [dualrun]. NUNCA cambia lo que se pinta — solo
// console.log/console.table, después de que el camino viejo ya resolvió.
//
// Retiro en el paso 3: borrar este archivo + los 2 call-sites que lo importan
// (VentasMiListaClientesV2/index.js y MapaCalor/hooks/useMapaCalorData.js).
// Todo detrás de DUAL_RUN_ACTIVIDADES — un solo `if` por call-site.
//
// Diferencias esperadas por las 5 decisiones de Ignacio (doc:
// docs/decisiones/2026-09-20-fase3b-diseno-actividad-precalculada.md):
//   1. Atraso — la actividad que explica la diferencia es posterior a
//      calculado_at (la tabla nueva tiene hasta 10 min de atraso).
//   2. Contacto relacional — el nuevo atribuye por un contacto que solo
//      existe en clientes_contactos_agencia/_cliente, no en las 4 columnas
//      legacy de `clientes`.
//   3. País estricto — el viejo atribuía por un contacto cuya actividad es
//      de OTRO país (rendija que solo tenía fetchAllActividadesBulk/CRM;
//      fetchUltimasActividadesBulk/Mapa ya filtraba país correctamente).
//   4. Día por huso — el grupo cambia de día. Solo aplica a CRM (Mapa no
//      agrupa por día). Perú: America/Lima contra hora de Chile (esperado,
//      decisión de Ignacio). Chile: bug real de SQL, no una decisión — ver
//      nota siguiente.
//   5. Tipo — actividad de un tipo fuera de MAIL/MEET/CALL (hoy no debería
//      haber ninguna).
// Cualquier diferencia que no caiga en 1-6 (ver abajo) es "sin explicación" → bug.
//
// BUG REAL DE SQL encontrado al investigar el CRM (2026-09-21, NO aplicado — decisión
// pendiente de Ignacio, ver docs/decisiones/2026-09-20-fase3b-diseno-actividad-precalculada.md
// "Ronda 5"): `crm_actividades.fecha` es `timestamp without time zone` (naive, sin zona). El
// SQL calcula el día con `(fecha AT TIME ZONE p_timezone)::date` — sobre una columna SIN zona
// eso hace una conversión doble (trata el valor naive como si YA fuera hora de Santiago/Lima,
// lo pasa a UTC, y el `::date` final lee ese UTC bajo la zona de la SESIÓN de Postgres, que es
// UTC) que corre el día hacia adelante cerca de la medianoche — afecta CHILE también, no solo
// Perú (antes se pensaba que Chile no tenía este problema porque "siempre usó su propio huso").
// La categoría "día por huso" de acá abajo se extendió a los DOS países porque la firma
// detectable (una actividad del mismo tipo en el día adyacente) es la misma para las dos causas.
//
// BUG REAL DE COMPARADOR, ya arreglado acá (ver `toMs` más abajo): comparar un `fecha` crudo
// (naive) contra un valor tageado de la DB (`calculado_at`/`ultima_fecha`/
// `ultima_fecha_del_grupo`, timestamptz) con `new Date().getTime()` sin normalizar corría por
// el offset Chile-UTC — inflaba "sin_explicacion" en las DOS ramas (CRM y Mapa).
//
// 2 categorías más, encontradas al investigar Mapa de Calor con datos reales
// (scripts/investigar-dualrun-mapacalor.mjs, 2026-09-21) — específicas de Mapa,
// CRM nunca las dispara:
//   6. Ventana 90d — `fetchUltimasActividadesBulk` (viejo, Mapa) filtra
//      `fecha >= hoy-90d`; `crm_actividad_resumen_contactos` no tiene ventana
//      (también alimenta el fallback "Último dd/mm/yy" del CRM, que necesita
//      historial completo). Un contacto sin actividad en 90+ días queda
//      invisible en el viejo; la tabla nueva sí trae su fecha real, aunque sea
//      vieja. Inocuo para el COLOR (UMBRALES tope 90 días, ambos casos caen en
//      rojo) — visible solo si se muestra la fecha cruda en algún momento.
//   7. Contacto ambiguo — el viejo de Mapa tiene un guard que el CRM nunca tuvo:
//      una actividad SIN `cliente_id` propio solo se atribuye si el contacto
//      pertenece a EXACTAMENTE 1 cliente ("evita falsos verdes cruzados").
//      Verificado 2026-09-21: explicaba el 100% de lo que antes caía en "sin
//      explicación" para Mapa Chile (341/341 filas). Ver el doc de Fase 3B,
//      sección "Investigación Mapa de Calor".
//
// REGLA APLICADA (2026-09-21, decisión de Ignacio, ver "Ronda 7" del doc de
// Fase 3B): la tabla nueva (`refrescar_resumen_actividad_pais`) AHORA tiene el
// mismo guard que Mapa siempre tuvo — una actividad sin `cliente_id` cuyo
// contacto atiende a 2+ clientes no se atribuye a NINGUNO (antes se repartía a
// todos). Efecto en el dual-run:
//   - Mapa: "contacto_ambiguo" debería bajar a ~0 — los dos lados ya excluyen
//     lo mismo, no hay más divergencia que explicar por esta causa. La rama de
//     abajo queda intacta (no hace falta tocarla) porque la divergencia que
//     detectaba ya no ocurre.
//   - CRM: aparece "contacto_ambiguo" por primera vez, y SUBE desde 0 — es lo
//     esperado, no un bug: el camino viejo del CRM (`ActividadesRecientes` vía
//     `agruparViejoPorLado`, réplica de `fetchAllActividadesBulk`) NUNCA tuvo
//     este guard y sigue repartiendo la actividad a todos los clientes del
//     contacto; la tabla nueva ya no. `viejoTieneMas` ahora puede explicarse
//     por esto, no solo por país estricto — ver `contactosAmbiguosLado` en
//     `clasificarDiferenciaCRM`.
// ═══════════════════════════════════════════════════════════════════════════

import { client } from ".";
import { buildContactMap } from "./getCrmActividades";

export const DUAL_RUN_ACTIVIDADES = true;

const CHUNK = 300;
const chunk = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

const fetchPorClienteIds = async (table, columns, clienteIds) => {
  if (clienteIds.length === 0) return [];
  const batches = chunk(clienteIds, CHUNK);
  const results = await Promise.all(
    batches.map(async (ids) => {
      const { data, error } = await client.from(table).select(columns).in("cliente_id", ids);
      if (error) {
        // eslint-disable-next-line no-console
        console.error(`[dualrun] error leyendo ${table}`, error);
        return [];
      }
      return data || [];
    })
  );
  return results.flat();
};

/** Lee las 2 tablas nuevas + las 2 tablas relacionales, filtradas por cliente_id. */
export const fetchDualRunData = async (clienteIds) => {
  const [grupos, contactos, relAgencia, relCliente] = await Promise.all([
    fetchPorClienteIds(
      "crm_actividad_resumen_grupos",
      "cliente_id,lado,tipo,dia,cantidad,ultima_fecha_del_grupo,calculado_at",
      clienteIds
    ),
    fetchPorClienteIds(
      "crm_actividad_resumen_contactos",
      "cliente_id,lado,usuario_id,ultima_fecha,calculado_at",
      clienteIds
    ),
    fetchPorClienteIds("clientes_contactos_agencia", "cliente_id,usuario_id", clienteIds),
    fetchPorClienteIds("clientes_contactos_cliente", "cliente_id,usuario_id", clienteIds),
  ]);
  return { grupos, contactos, relAgencia, relCliente };
};

// ─── Helpers de índice ────────────────────────────────────────────────────

const indexBy2 = (rows, k1, k2) => {
  const map = {};
  rows.forEach((r) => {
    const a = r[k1];
    const b = r[k2];
    if (!map[a]) map[a] = {};
    if (!map[a][b]) map[a][b] = [];
    map[a][b].push(r);
  });
  return map;
};

/** Agrupa filas por cliente_id → array. Para tablas relacionales (solo esa key). */
const groupByClienteId = (rows) => {
  const map = {};
  rows.forEach((r) => {
    if (!map[r.cliente_id]) map[r.cliente_id] = [];
    map[r.cliente_id].push(r);
  });
  return map;
};

/** Set de usuario_id que son contacto relacional de (cliente,lado) pero NO están en las 4 legacy. */
const contactosSoloRelacionales = (cliente, lado, relRowsDelCliente) => {
  const legacyIds = new Set(
    (lado === "agencia"
      ? [cliente.contacto_agencia, cliente.contacto_agencia_1, cliente.contacto_agencia_2, cliente.contacto_agencia_3]
      : [cliente.contacto_cliente, cliente.contacto_cliente_1, cliente.contacto_cliente_2, cliente.contacto_cliente_3]
    ).filter(Boolean)
  );
  const relIds = (relRowsDelCliente || []).map((r) => r.usuario_id);
  return new Set(relIds.filter((id) => !legacyIds.has(id)));
};

// ─── CRM: grupos (tipo, día) por lado, top 5 — réplica de ActividadesRecientes ──

const getDateKeyOld = (fecha) => {
  if (!fecha) return "";
  const date = new Date(fecha);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

/** "YYYY-MM-DD" + offset de días (puede ser negativo) → "YYYY-MM-DD". */
const diaMasOffset = (diaKey, offsetDias) => {
  const [y, m, d] = diaKey.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  date.setDate(date.getDate() + offsetDias);
  return getDateKeyOld(date);
};

// Comparaciones de fecha por timestamp numérico, NUNCA por string: el viejo (REST directo) y el
// nuevo (supabase-js sobre las mismas tablas) pueden serializar el mismo instante con distinta
// cantidad de decimales/sufijo — comparar el string haría ver diferencias que no existen.
//
// BUG REAL encontrado e investigado en detalle (2026-09-21, investigación CRM): `crm_actividades.fecha`
// es `timestamp without time zone` (naive) — se sirve SIN sufijo de zona ("2026-04-17 20:06:00").
// `calculado_at`/`ultima_fecha`/`ultima_fecha_del_grupo` son `timestamptz` — se sirven CON sufijo
// ("...+00"). `new Date(x)` interpreta un string SIN sufijo como hora LOCAL del browser (Chile) — uno
// CON sufijo, como UTC real. Sin normalizar, comparar un `fecha` crudo contra un valor ya tageado de
// la DB corre por el offset Chile-UTC (3-4h) aunque sea el MISMO instante — confirmado: 2026-04-17
// 20:06:00 (naive, interpretado en Chile) da 1776470760000; 2026-04-17T20:06:00+00:00 (tageado) da
// 1776456360000, 4h de diferencia, mismo evento real. Mismo bug de raíz ya encontrado y arreglado
// SOLO en el script de investigación de Mapa de Calor (scripts/investigar-dualrun-mapacalor.mjs,
// 2026-09-21) pero nunca corregido acá — inflaba "sin_explicacion" en las DOS ramas (CRM y Mapa) en
// cualquier comparación que mezclara un `fecha` crudo contra un valor tageado (el fallback "Último
// dd/mm/yy" y la categoría "atraso"): medido en la investigación CRM, arreglar esto solo ya bajó
// sin_explicacion de 774 a 393 filas sobre un universo de prueba de 586 clientes Chile.
const toMs = (fecha) => {
  if (!fecha) return null;
  const trimmed = String(fecha).trim();
  const tieneTZ = /([Zz]|[+-]\d{2}(:?\d{2})?)$/.test(trimmed);
  const normalizado = tieneTZ ? trimmed : `${trimmed.replace(" ", "T")}Z`;
  return new Date(normalizado).getTime();
};
const fechaMayor = (a, b) => {
  const msA = toMs(a);
  const msB = toMs(b);
  if (msA === null) return false;
  if (msB === null) return true;
  return msA > msB;
};
const fechasIguales = (a, b) => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return toMs(a) === toMs(b);
};
const fechaMasReciente = (a, b) => (fechaMayor(a, b) ? a : b);

/** Réplica de ActividadesRecientes.filtrar + agruparActividades, para un lado. Excluye SALE. */
const agruparViejoPorLado = (actividades, cliente, lado) => {
  const contactIds = new Set(
    (lado === "agencia"
      ? [cliente.contacto_agencia, cliente.contacto_agencia_1, cliente.contacto_agencia_2, cliente.contacto_agencia_3]
      : [cliente.contacto_cliente, cliente.contacto_cliente_1, cliente.contacto_cliente_2, cliente.contacto_cliente_3]
    ).filter(Boolean)
  );
  if (contactIds.size === 0) return [];

  const filtradas = actividades.filter((a) => {
    if (a.tipo === "SALE") return false; // fuera de la tabla precalculada (sección 1.3 del diseño)
    const destinatarios = a.destinatarios || [];
    return destinatarios.some((d) => contactIds.has(d.usuario_id));
    // Nota: no comparamos por email acá — el viejo también matchea por email de contacto, pero
    // el dual-run no tiene el mapa email→cliente a mano en este punto; subestima levemente el
    // viejo (nunca lo sobreestima), lo que en el peor caso genera un falso "nuevo trae más" que
    // se clasifica igual (Contacto relacional u otra categoría) — no genera falsos "sin explicación".
  });

  const grupos = {};
  filtradas.forEach((a) => {
    const dia = getDateKeyOld(a.fecha);
    const key = `${a.tipo}|${dia}`;
    if (!grupos[key]) grupos[key] = { tipo: a.tipo, dia, cantidad: 0, fecha: a.fecha, actividades: [] };
    grupos[key].cantidad += 1;
    grupos[key].actividades.push(a);
    if (fechaMayor(a.fecha, grupos[key].fecha)) grupos[key].fecha = a.fecha;
  });

  return Object.values(grupos)
    .sort((a, b) => new Date(b.fecha) - new Date(a.fecha))
    .slice(0, 5);
};

const gruposNuevosPorLado = (grupos, clienteId, lado) =>
  [...(grupos[clienteId]?.[lado] || [])]
    .sort((a, b) => new Date(b.ultima_fecha_del_grupo) - new Date(a.ultima_fecha_del_grupo))
    .slice(0, 5);

// ─── Clasificación de una diferencia puntual (tipo, día) ──────────────────

const TIPOS_CONOCIDOS = new Set(["MAIL", "MEET", "CALL"]);

/**
 * Clasifica una diferencia (viejo vs nuevo) para un grupo (tipo,día) de un cliente/lado.
 * Orden fijo (el que realmente ejecuta el código): atraso → tipo → día por huso (Chile Y Perú,
 * ver nota arriba) → contacto relacional → país estricto → sin explicación.
 */
const clasificarDiferenciaCRM = ({
  tipo,
  dia,
  viejoGrupo,
  nuevoGrupo,
  cliente,
  paisCode,
  calculadoAt,
  actividadesViejas,
  soloRelacionales,
  contactosAmbiguosLado,
}) => {
  // 1. Atraso: hay una actividad del viejo (de este tipo) posterior a calculado_at que el
  // nuevo todavía no vio (típico cuando el viejo tiene el grupo y el nuevo no, o tiene menos).
  if (calculadoAt) {
    const masReciente = (actividadesViejas || []).find(
      (a) => a.tipo === tipo && getDateKeyOld(a.fecha) === dia && fechaMayor(a.fecha, calculadoAt)
    );
    if (masReciente) return { categoria: "atraso", detalle: `actividad ${masReciente.fecha} > calculado_at` };
  }

  // 5. Tipo: fuera de MAIL/MEET/CALL
  if (!TIPOS_CONOCIDOS.has(tipo)) {
    return { categoria: "tipo", detalle: `tipo=${tipo}` };
  }

  // 4. Día por huso — evidencia real (no sospecha): hay una actividad VIEJA de este tipo
  // bucketeada un día antes o después. Antes solo se chequeaba para Perú (América/Santiago vs
  // América/Lima). Investigación CRM 2026-09-21 (docs/decisiones/2026-09-20-fase3b-diseno-
  // actividad-precalculada.md, "Ronda 5"): CHILE tiene su PROPIO bug de día, distinto al de
  // Perú — `crm_actividades.fecha` es `timestamp without time zone` (naive) y el SQL calcula
  // el día con `(fecha AT TIME ZONE 'America/Santiago')::date`, que para una columna SIN zona
  // hace una conversión doble (naive→tratada como Santiago→UTC, más el cast a fecha bajo la
  // zona de la SESIÓN de Postgres, UTC) que corre el día hacia adelante cerca de la
  // medianoche — confirmado con casos reales (ver doc). La FIRMA detectable (una actividad
  // cruza a un día adyacente) es la misma que la de Perú, aunque la causa de fondo sea otra —
  // se reusa el mismo chequeo para los dos países. El SQL no se toca en esta investigación
  // (bug real, propuesto para decisión de Ignacio, no aplicado); esto solo clasifica
  // correctamente lo que YA es una diferencia esperada en vez de dejarla como "sin explicación".
  const diaAntes = diaMasOffset(dia, -1);
  const diaDespues = diaMasOffset(dia, 1);
  const enDiaAdyacente = (actividadesViejas || []).some(
    (a) => a.tipo === tipo && [diaAntes, diaDespues].includes(getDateKeyOld(a.fecha))
  );
  if (enDiaAdyacente) {
    return { categoria: "dia_huso", detalle: `actividad del mismo tipo en ${diaAntes}/${diaDespues}` };
  }

  // Nuevo trae MÁS (o algo que el viejo no tiene) → candidato: contacto relacional
  const nuevoTieneMas = !!nuevoGrupo && (!viejoGrupo || nuevoGrupo.cantidad > viejoGrupo.cantidad);
  if (
    nuevoTieneMas && // 2. Contacto relacional: hay al menos un contacto de este lado que solo existe en la relacional
    soloRelacionales &&
    soloRelacionales.size > 0
  ) {
    return { categoria: "contacto_relacional", detalle: `${soloRelacionales.size} contacto(s) solo relacional` };
  }

  // Viejo trae MÁS (o algo que el nuevo no tiene) → candidatos: país estricto, contacto ambiguo
  const viejoTieneMas = !!viejoGrupo && (!nuevoGrupo || viejoGrupo.cantidad > nuevoGrupo.cantidad);
  if (viejoTieneMas) {
    const deOtroPais = (viejoGrupo.actividades || []).some((a) => a.pais && a.pais !== paisCode);
    if (deOtroPais) return { categoria: "pais_estricto", detalle: "actividad de otro país en el grupo viejo" };

    // Contacto ambiguo (regla 2026-09-21): el viejo (agruparViejoPorLado, réplica de
    // fetchAllActividadesBulk) nunca tuvo el guard "descartar si el contacto atiende a 2+
    // clientes" — sigue repartiendo. La tabla nueva ya no atribuye esa actividad a NINGÚN
    // cliente por esta vía. Evidencia: alguna actividad del grupo viejo, sin cliente_id
    // propio, con un destinatario que es un contacto ambiguo de este lado — chequeado por
    // usuario_id Y por email (muchos `crm_actividades_destinatarios` solo tienen email, sin
    // usuario_id resuelto — matchear solo por id subdetecta y deja casos reales en "sin
    // explicación", verificado con el caso TOYOTA/430 + Magdalena Silva, 2026-09-21).
    if (contactosAmbiguosLado && (contactosAmbiguosLado.ids.size > 0 || contactosAmbiguosLado.emails.size > 0)) {
      const porContactoAmbiguo = (viejoGrupo.actividades || []).some(
        (a) =>
          !a.cliente_id &&
          (a.destinatarios || []).some(
            (d) =>
              contactosAmbiguosLado.ids.has(d.usuario_id) ||
              (d.email && contactosAmbiguosLado.emails.has(d.email.toLowerCase()))
          )
      );
      if (porContactoAmbiguo) {
        return {
          categoria: "contacto_ambiguo",
          detalle: "contacto que atiende a 2+ clientes, actividad sin cliente_id",
        };
      }
    }
  }

  return { categoria: "sin_explicacion", detalle: "" };
};

// ─── Logging ───────────────────────────────────────────────────────────────

const CATEGORIAS_ORDEN = [
  "atraso",
  "contacto_relacional",
  "pais_estricto",
  "dia_huso",
  "tipo",
  "ventana_90d", // solo Mapa — nota 6 de la cabecera
  "contacto_ambiguo", // nota 7 de la cabecera — desde la regla 2026-09-21, también en CRM
  "sin_explicacion",
];

const nuevoResumen = () =>
  Object.fromEntries(CATEGORIAS_ORDEN.map((c) => [c, { total: 0, clientes: new Set(), ejemplos: [] }]));

// Unidad del resumen: filas Y clientes distintos por separado (una fila = una diferencia
// puntual; un cliente puede aportar varias filas — reportar solo "filas" exagera el impacto
// real en pantalla, que es por cliente).
const registrar = (resumen, categoria, ejemplo, clienteId) => {
  resumen[categoria].total += 1;
  resumen[categoria].clientes.add(clienteId);
  if (resumen[categoria].ejemplos.length < 5) resumen[categoria].ejemplos.push(ejemplo);
};

const logResumen = (pantalla, totalClientes, identicos, resumen, filasDetalle) => {
  // eslint-disable-next-line no-console
  console.log(
    `[dualrun][${pantalla}] clientes comparados: ${totalClientes} — idénticos: ${identicos} — ` +
      CATEGORIAS_ORDEN.map((c) => `${c}: ${resumen[c].total} filas / ${resumen[c].clientes.size} clientes`).join(" · ")
  );
  CATEGORIAS_ORDEN.forEach((c) => {
    if (resumen[c].ejemplos.length > 0) {
      // eslint-disable-next-line no-console
      console.log(`[dualrun][${pantalla}] ejemplos "${c}":`, resumen[c].ejemplos);
    }
  });
  if (filasDetalle.length > 0) {
    // eslint-disable-next-line no-console
    console.log(`[dualrun][${pantalla}] detalle completo (${filasDetalle.length} filas) — console.table:`);
    // eslint-disable-next-line no-console
    console.table(filasDetalle);
  }
};

// ─── Entry point: CRM (ActividadesRecientes + fallback "Último dd/mm/yy") ──

export const compararDualRunCRM = ({ clientes, actividadesCRM, ultimaActividadPorLado, paisCode, usuarios = [] }) => {
  if (!DUAL_RUN_ACTIVIDADES) return;

  const clienteIds = clientes.map((c) => c.id);
  fetchDualRunData(clienteIds)
    .then(({ grupos, contactos, relAgencia, relCliente }) => {
      const gruposPorCliente = {};
      grupos.forEach((g) => {
        if (!gruposPorCliente[g.cliente_id]) gruposPorCliente[g.cliente_id] = { agencia: [], cliente: [] };
        gruposPorCliente[g.cliente_id][g.lado].push(g);
      });
      const contactosNewByCliente = indexBy2(contactos, "cliente_id", "lado");
      const relAgenciaFlat = groupByClienteId(relAgencia);
      const relClienteFlat = groupByClienteId(relCliente);

      // contactosAmbiguosLado: contactos que atienden 2+ clientes distintos en ESTE lado, sobre
      // el universo relacional completo de los clientes comparados (todo el país — clienteIds
      // acá es TODO el país). Misma definición que usa la migración
      // (_contactos_lado_no_ambiguo: GROUP BY usuario_id,lado HAVING count(DISTINCT cliente_id)=1).
      // Se guarda por `ids` (usuario_id) Y por `emails` (lowercase) — muchas filas de
      // `crm_actividades_destinatarios` solo tienen email, sin usuario_id resuelto; matchear
      // solo por id subdetecta (verificado: caso TOYOTA/430 + Magdalena Silva, 2026-09-21).
      const emailByUsuarioId = {};
      usuarios.forEach((u) => {
        if (u.email) emailByUsuarioId[u.id] = u.email.toLowerCase();
      });
      const contarAmbiguos = (rows) => {
        const map = {};
        rows.forEach((r) => {
          if (!map[r.usuario_id]) map[r.usuario_id] = new Set();
          map[r.usuario_id].add(r.cliente_id);
        });
        const ids = new Set();
        const emails = new Set();
        Object.entries(map).forEach(([usuarioId, clienteIdsSet]) => {
          if (clienteIdsSet.size > 1) {
            const uid = Number(usuarioId);
            ids.add(uid);
            if (emailByUsuarioId[uid]) emails.add(emailByUsuarioId[uid]);
          }
        });
        return { ids, emails };
      };
      const contactosAmbiguosPorLado = {
        agencia: contarAmbiguos(relAgencia),
        cliente: contarAmbiguos(relCliente),
      };

      const resumen = nuevoResumen();
      const filasDetalle = [];
      let identicos = 0;

      clientes.forEach((cliente) => {
        let clienteIdentico = true;

        ["agencia", "cliente"].forEach((lado) => {
          const viejoTop5 = agruparViejoPorLado(actividadesCRM[cliente.id] || [], cliente, lado);
          const nuevoTop5 = gruposNuevosPorLado(
            gruposPorCliente[cliente.id] || { agencia: [], cliente: [] },
            cliente.id,
            lado
          );
          const calculadoAt = nuevoTop5[0]?.calculado_at || gruposPorCliente[cliente.id]?.[lado]?.[0]?.calculado_at;

          const viejoByKey = Object.fromEntries(viejoTop5.map((g) => [`${g.tipo}|${g.dia}`, g]));
          const nuevoByKey = Object.fromEntries(nuevoTop5.map((g) => [`${g.tipo}|${g.dia}`, g]));
          const todasLasKeys = new Set([...Object.keys(viejoByKey), ...Object.keys(nuevoByKey)]);

          const relRowsDelLado = lado === "agencia" ? relAgenciaFlat : relClienteFlat;
          const soloRel = contactosSoloRelacionales(cliente, lado, relRowsDelLado[cliente.id]);

          todasLasKeys.forEach((key) => {
            const viejoGrupo = viejoByKey[key];
            const nuevoGrupo = nuevoByKey[key];
            const mismaCantidad = viejoGrupo && nuevoGrupo && viejoGrupo.cantidad === nuevoGrupo.cantidad;
            if (mismaCantidad) return; // idéntico en este (tipo,día)

            clienteIdentico = false;
            const [tipo, dia] = key.split("|");
            const { categoria, detalle } = clasificarDiferenciaCRM({
              tipo,
              dia,
              viejoGrupo,
              nuevoGrupo,
              cliente,
              paisCode,
              calculadoAt,
              actividadesViejas: actividadesCRM[cliente.id] || [],
              soloRelacionales: soloRel,
              contactosAmbiguosLado: contactosAmbiguosPorLado[lado],
            });

            const fila = {
              cliente: cliente.empresa,
              cliente_id: cliente.id,
              lado,
              tipo,
              dia,
              viejo: viejoGrupo ? viejoGrupo.cantidad : "-",
              nuevo: nuevoGrupo ? nuevoGrupo.cantidad : "-",
              categoria,
              detalle,
            };
            filasDetalle.push(fila);
            registrar(resumen, categoria, fila, cliente.id);
          });

          // Fallback "Último dd/mm/yy"
          const viejoUltima = ultimaActividadPorLado[cliente.id]?.[lado] || null;
          const nuevoUltima = (contactosNewByCliente[cliente.id]?.[lado] || []).reduce(
            (max, c) => fechaMasReciente(max, c.ultima_fecha),
            null
          );
          if ((viejoUltima || nuevoUltima) && !fechasIguales(viejoUltima, nuevoUltima)) {
            // Solo importa cuando NO hay grupos recientes (es cuando se pinta) — igual lo reportamos siempre,
            // clasificado, para no esconder nada.
            clienteIdentico = false;
            const calculadoAtFallback = contactosNewByCliente[cliente.id]?.[lado]?.[0]?.calculado_at;
            let categoria = "sin_explicacion";
            if (calculadoAtFallback && fechaMayor(viejoUltima, calculadoAtFallback)) categoria = "atraso";
            else if (soloRel.size > 0 && (!viejoUltima || fechaMayor(nuevoUltima, viejoUltima))) {
              categoria = "contacto_relacional";
            } else if (contactosAmbiguosPorLado[lado] && viejoUltima) {
              // contacto_ambiguo en el fallback: la actividad que produjo viejoUltima (sin
              // cliente_id, destinatario ambiguo de este lado) ya no cuenta en la tabla nueva.
              const ambiguosLado = contactosAmbiguosPorLado[lado];
              const porContactoAmbiguo = (actividadesCRM[cliente.id] || []).some(
                (a) =>
                  !a.cliente_id &&
                  fechasIguales(a.fecha, viejoUltima) &&
                  (a.destinatarios || []).some(
                    (d) =>
                      ambiguosLado.ids.has(d.usuario_id) || (d.email && ambiguosLado.emails.has(d.email.toLowerCase()))
                  )
              );
              if (porContactoAmbiguo) categoria = "contacto_ambiguo";
            }
            const fila = {
              cliente: cliente.empresa,
              cliente_id: cliente.id,
              lado,
              tipo: "(fallback)",
              dia: "-",
              viejo: viejoUltima || "-",
              nuevo: nuevoUltima || "-",
              categoria,
              detalle: "Último dd/mm/yy",
            };
            filasDetalle.push(fila);
            registrar(resumen, categoria, fila, cliente.id);
          }
        });

        if (clienteIdentico) identicos += 1;
      });

      logResumen("CRM", clientes.length, identicos, resumen, filasDetalle);
    })
    .catch((e) => {
      // eslint-disable-next-line no-console
      console.error("[dualrun][CRM] error en la comparación", e);
    });
};

// ─── Entry point: Mapa de Calor (última fecha por contacto y por lado) ────

const NOVENTA_DIAS_MS = 90 * 24 * 60 * 60 * 1000;

export const compararDualRunMapaCalor = ({ clientes, usuarios, actividades, paisCode, contactosAgencia = [] }) => {
  if (!DUAL_RUN_ACTIVIDADES) return;

  // contactMap: EXACTAMENTE el mismo cálculo que usa fetchUltimasActividadesBulk (mismo
  // buildContactMap, mismo contactosAgencia) — "contacto relacional" se decide contra este
  // set real, no contra un diff genérico legacy-vs-relacional (ese era el bug: para el lado
  // agencia, buildContactMap prefiere la relacional COMPLETA cuando existe — un contacto
  // "solo relacional" ya está visible para el viejo si el cliente tiene alguna fila
  // relacional de agencia; el diff genérico lo marcaba "contacto_relacional" igual,
  // sobreestimando esa categoría — verificado: 132 reportados vs 0 reales en Chile,
  // 2026-09-21).
  const contactMap = buildContactMap(clientes, usuarios, contactosAgencia);

  // emailToClienteIds: para detectar "contacto ambiguo" (nota 7 de la cabecera) — un
  // contacto que es destinatario de una actividad SIN cliente_id propio, compartido por
  // más de 1 cliente, queda descartado por el viejo (guard "evita falsos verdes cruzados")
  // pero la tabla nueva lo reparte a todos. No hace falta la actividad cruda para esto: el
  // conteo de clientes por contacto ya lo dice.
  const emailToClienteIds = {};
  clientes.forEach((c) => {
    (contactMap[c.id]?.emails || []).forEach((email) => {
      if (!emailToClienteIds[email]) emailToClienteIds[email] = [];
      emailToClienteIds[email].push(c.id);
    });
  });

  const clienteIds = clientes.map((c) => c.id);
  fetchDualRunData(clienteIds)
    .then(({ contactos, relAgencia, relCliente }) => {
      const usuariosMap = Object.fromEntries(usuarios.map((u) => [u.id, u]));
      const contactosNewByCliente = indexBy2(contactos, "cliente_id", "lado");
      const relAgenciaByCliente = groupByClienteId(relAgencia);
      const relClienteByCliente = groupByClienteId(relCliente);

      const resumen = nuevoResumen();
      const filasDetalle = [];
      let identicos = 0;
      const ahoraMs = Date.now();

      clientes.forEach((cliente) => {
        let clienteIdentico = true;
        const actPerEmail = actividades[cliente.id] || {};

        [
          ["agencia", relAgenciaByCliente],
          ["cliente", relClienteByCliente],
        ].forEach(([lado, relByCliente]) => {
          const relRows = relByCliente[cliente.id] || [];
          const nuevoRowsByUsuario = Object.fromEntries(
            (contactosNewByCliente[cliente.id]?.[lado] || []).map((c) => [c.usuario_id, c])
          );

          relRows.forEach((rel) => {
            const usuario = usuariosMap[rel.usuario_id];
            if (!usuario?.email) return;
            const email = usuario.email.toLowerCase();

            // enContactMapViejo: el contacto está realmente en el set que buildContactMap
            // calculó para este cliente (fuente real de "contacto relacional").
            const enContactMapViejo = (contactMap[cliente.id]?.userIds || []).includes(rel.usuario_id);
            const viejo = enContactMapViejo ? actPerEmail[email] || null : null;
            const nuevoRow = nuevoRowsByUsuario[rel.usuario_id];
            const nuevo = nuevoRow?.ultima_fecha || null;

            if (fechasIguales(viejo, nuevo)) return;

            clienteIdentico = false;
            let categoria = "sin_explicacion";
            const calculadoAt = nuevoRow?.calculado_at;
            // País estricto: fetchUltimasActividadesBulk (viejo) ya filtra `pais=eq.${paisCode}`
            // en su propia query — estructuralmente no puede mostrar actividad de otro país.
            // No hay una rama para esta categoría en Mapa a propósito (no una omisión — sí la
            // había en CRM, que tiene la rendija real); verificado con datos reales, 2026-09-21:
            // 0 de 3.293 actividades del universo (Chile) eran de otro país.
            if (calculadoAt && fechaMayor(viejo, calculadoAt)) {
              categoria = "atraso";
            } else if (!enContactMapViejo) {
              categoria = "contacto_relacional";
            } else if (nuevo && ahoraMs - new Date(nuevo).getTime() > NOVENTA_DIAS_MS) {
              categoria = "ventana_90d";
            } else if ((emailToClienteIds[email] || []).length > 1) {
              categoria = "contacto_ambiguo";
            }

            const fila = {
              cliente: cliente.empresa,
              cliente_id: cliente.id,
              lado,
              contacto: `${usuario.nombre || ""} ${usuario.apellido || ""}`.trim() || usuario.email,
              viejo: viejo || "-",
              nuevo: nuevo || "-",
              categoria,
              detalle: "última fecha por contacto",
            };
            filasDetalle.push(fila);
            registrar(resumen, categoria, fila, cliente.id);
          });
        });

        if (clienteIdentico) identicos += 1;
      });

      logResumen("MapaCalor", clientes.length, identicos, resumen, filasDetalle);
    })
    .catch((e) => {
      // eslint-disable-next-line no-console
      console.error("[dualrun][MapaCalor] error en la comparación", e);
    });
};
